import { IForwardRep } from '@bb/users';
import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  Avatar,
  Text,
  DatePicker,
  Divider,
  Icon,
  UserSelect,
  Form,
} from 'components';
import { AgentsContext, AgentsContextUser } from 'contexts';
import { useBreakpoint } from 'hooks';
import { endOfDay, startOfDay } from 'date-fns/esm';
import { SectionContainer, StyledIcon, StyledButton } from './StyledComponents';
import { ERROR_MESSAGES } from '../constants';
import { ForwardRepDirty } from '../types';
import FormContext, { FormContextType } from '../FormContext';
import { getDate } from './helpers';

export type RepType = IForwardRep;

interface ForwardRepProps {
  isLast: boolean;
  selectedRep?: IForwardRep;
  onRepSelect: (user: RepType) => void;
  onRemoveRep: () => void;
  selectedRepIds: string[];
  dirtyState: ForwardRepDirty;
  setDirtyState: (field: keyof ForwardRepDirty) => void;
  validityState: FormContextType['frontDeskValidState']['forwardReps'][number];
}

const getExpiryTimeMessage = (
  dirtyState: ForwardRepDirty,
  validityState: {
    id: boolean;
    isStartBeforeExpiry: boolean;
  },
) => {
  let expiryTimeErrorMessage: string | undefined;
  if (dirtyState.forwardRepsExpiry && !validityState.isStartBeforeExpiry) {
    expiryTimeErrorMessage = ERROR_MESSAGES.invalidDateRange;
  }
  return expiryTimeErrorMessage;
};

const ForwardRep = ({
  isLast,
  selectedRep,
  onRepSelect,
  onRemoveRep,
  selectedRepIds,
  dirtyState,
  setDirtyState,
  validityState,
}: ForwardRepProps) => {
  const { userId } = useContext(FormContext);
  const { orgUsersMap, fetchHiddenUser } = useContext(AgentsContext);
  const { md } = useBreakpoint();
  const updateRep = (
    field: keyof RepType,
    value: string | Date | undefined | null,
  ) => {
    onRepSelect({ ...selectedRep!, [field]: value });
  };
  const [displaySelected, setDisplaySelected] = useState(true);
  const [mappedRep, setMappedRep] = useState<AgentsContextUser>();

  const fetchUser = async (hiddenUserId: string) => {
    const user = await fetchHiddenUser!(hiddenUserId);
    setMappedRep(user);
  };

  useEffect(() => {
    // if still fetching, return
    if (!selectedRep?.id || !Object.keys(orgUsersMap!).length) return;

    if (orgUsersMap![selectedRep.id]) {
      setMappedRep(orgUsersMap![selectedRep.id]);
      return;
    }

    // special case where the forwarded rep is outside of the current user visibility
    fetchUser(selectedRep.id);
  }, [selectedRep?.id, orgUsersMap]);

  const selectedValue =
    selectedRep && selectedRep.id ? (
      <SelectedValueContainer hidden={!displaySelected}>
        <StyledAvatar width={16} user={mappedRep} />
        <StyledFullName>
          {[mappedRep?.firstName || '', mappedRep?.lastName || ''].join(' ')}
        </StyledFullName>
      </SelectedValueContainer>
    ) : undefined;

  const displayUserSelectError = dirtyState.id && !validityState.id;

  const expiryTimeErrorMessage = getExpiryTimeMessage(
    dirtyState,
    validityState,
  );

  const today = startOfDay(new Date());
  const startDateTime = getDate(selectedRep?.forwardRepsStart);
  const expiryDateTime = getDate(selectedRep?.forwardRepsExpiry);

  return (
    <SectionContainer>
      <RepSelectContainer>
        <Text>Forwards to</Text>
        <Form.Item
          validateStatus={displayUserSelectError ? 'error' : undefined}
          help={displayUserSelectError ? ERROR_MESSAGES.forwardUser : undefined}
        >
          <UserSelect
            onFocus={() => setDisplaySelected(false)}
            showSearch
            onChange={ids => updateRep('id', ids?.[0])}
            value={
              selectedValue
                ? { value: selectedRep!.id, label: selectedValue }
                : undefined
            }
            onClear={() => updateRep('id', undefined)}
            clearIcon={<StyledIcon type="antdCloseCircleFilled" width={14} />}
            showArrow={false}
            allowClear
            filter={rep =>
              !selectedRepIds.includes(rep.id) && rep.id !== userId
            } // Don't allow the same rep to be selected twice, or allow a user to be their own forward rep
            onBlur={() => {
              setDirtyState('id');
              setDisplaySelected(true);
            }}
            suppressInitialFetch
          />
        </Form.Item>
      </RepSelectContainer>
      <MaxWidthFormItem>
        <DatePicker
          label={
            <TextContainer>
              <Text>Starts</Text>
            </TextContainer>
          }
          value={getDate(startDateTime)}
          onChange={date => updateRep('forwardRepsStart', date)}
          disabledDate={date => {
            if (expiryDateTime && expiryDateTime > today) {
              return !(date > today && date < expiryDateTime);
            }
            return today > date;
          }}
          format="MMM D, YYYY HH:mm"
          placeholder={md ? 'Select date & time' : 'Select'}
          onBlur={() => setDirtyState('forwardRepsStart')}
          suffixIcon={<StyledIcon type="calendar" width={12} />}
          picker="date"
          showTime={{ defaultValue: startOfDay(new Date()) }}
          showNow={false}
        />
      </MaxWidthFormItem>
      <MaxWidthFormItem
        validateStatus={expiryTimeErrorMessage ? 'error' : undefined}
        help={expiryTimeErrorMessage}
      >
        <DatePicker
          label={
            <TextContainer>
              <Text>Ends</Text>
            </TextContainer>
          }
          value={getDate(expiryDateTime)}
          onChange={date => updateRep('forwardRepsExpiry', date)}
          disabledDate={date => {
            if (startDateTime) return date < startOfDay(startDateTime);
            return today > date;
          }}
          format="MMM D, YYYY HH:mm"
          placeholder={md ? 'Select date & time' : 'Select'}
          onBlur={() => setDirtyState('forwardRepsExpiry')}
          suffixIcon={<StyledIcon type="calendar" width={12} />}
          picker="date"
          showTime={{ defaultValue: endOfDay(new Date()) }}
          showNow={false}
        />
      </MaxWidthFormItem>
      {isLast && (
        <LastItemContainer>
          <Form.Item>
            <StyledButton
              type="link"
              danger
              icon={
                <Icon
                  type="antdMinusCircleOutlined"
                  color="danger"
                  width={14}
                />
              }
              onClick={onRemoveRep}
            >
              Remove Forward
            </StyledButton>
          </Form.Item>
        </LastItemContainer>
      )}
      <StyledDivider />
    </SectionContainer>
  );
};

const MaxWidthFormItem = styled(Form.Item)`
  max-width: 200px;
`;

const LastItemContainer = styled.div`
  grid-column: 1 / span 2;
`;

const StyledDivider = styled(Divider)`
  grid-column: 1 / span 2;
  margin-top: 0px;
`;
const TextContainer = styled.div`
  margin-bottom: 8px;
`;

const SelectedValueContainer = styled.div`
  display: flex;
  align-items: center;
`;
const StyledAvatar = styled(Avatar)``;

const StyledFullName = styled(Text)`
  margin-left: 5px;
`;

const RepSelectContainer = styled.div`
  grid-column: 1 / span 2;
  > :not(:first-child) {
    margin-top: 8px;
  }
`;
export default React.memo(ForwardRep);
